import { z } from "zod";

// Client-intake form schema (Story 5.7 + spec-gh-352). Mirrors the full Screen 03
// intake form and the backend `CreateClientRequest`, blocking the same inputs the
// API would reject, with matching messages:
//  - names: whitespace-trimmed, non-blank, 1–100 chars (server `_Name`)
//  - middle initial: optional, ≤100 chars (PHI; blank → omitted)
//  - dob: a real calendar date, on/after 1900-01-01 (`implausible_dob`), not in
//    the future (`future_dob`)
//  - gender: optional select (female | male | other)
//  - notes: optional free text, ≤2000 chars (PHI; blank → omitted)
//
// The readable Client ID (CITS-NNNNNN, FR12) is a fast-follow and is NOT collected
// here. The "Save & start evaluation" CTA stays unbuilt — client creation is
// decoupled from starting an evaluation (FR6/FR13).

const NAME_MAX = 100;
/** Earliest accepted birth date — mirrors the server's `implausible_dob` bound. */
export const MIN_DOB = "1900-01-01";

/** Today's date as an ISO `YYYY-MM-DD` string in UTC — matches the server's UTC bound. */
export function todayUtcIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** True only for a well-formed `YYYY-MM-DD` string that is a real calendar date. */
export function isIsoCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  // Round-trip guards against overflow dates the regex alone allows (e.g. 2021-02-30).
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

const name = (field: string) =>
  z
    .string()
    .trim()
    .min(1, `Enter the client's ${field}.`)
    .max(NAME_MAX, `${field[0].toUpperCase()}${field.slice(1)} must be ${NAME_MAX} characters or fewer.`);

// Closed value set for the Screen 03 gender select — mirrors the backend Literal.
// Exported so the screen renders the option list from a single source of truth.
export const GENDER_VALUES = ["female", "male", "other"] as const;

const MIDDLE_INITIAL_MAX = 100;
const NOTES_MAX = 2000;

export const clientIntakeSchema = z.object({
  firstName: name("first name"),
  // Optional bounded string; blank stays "" here and is omitted at submit time so
  // the API never receives an empty-string PHI value to encrypt.
  middleInitial: z
    .string()
    .trim()
    .max(
      MIDDLE_INITIAL_MAX,
      `Middle initial must be ${MIDDLE_INITIAL_MAX} characters or fewer.`,
    ),
  lastName: name("last name"),
  // `<input type="date">` emits "" or a valid YYYY-MM-DD; the superRefine covers
  // both that and any value a test (or pasted input) might supply. ISO date strings
  // compare correctly with `<`/`>`, so no Date math/timezone hazard is introduced.
  dob: z.string().superRefine((value, ctx) => {
    if (!value) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter the client's date of birth." });
      return;
    }
    if (!isIsoCalendarDate(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a valid date." });
      return;
    }
    if (value < MIN_DOB) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Date of birth must be on or after 1900-01-01.",
      });
      return;
    }
    if (value > todayUtcIso()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Date of birth cannot be in the future.",
      });
    }
  }),
  // Optional select: "" (placeholder) is allowed and omitted at submit time.
  gender: z.union([z.enum(GENDER_VALUES), z.literal("")]),
  // Optional free text; blank stays "" here and is omitted at submit time.
  notes: z
    .string()
    .trim()
    .max(NOTES_MAX, `Notes must be ${NOTES_MAX} characters or fewer.`),
});

export type ClientIntakeValues = z.infer<typeof clientIntakeSchema>;
